import { ChangeDetectionStrategy, Component, Input, forwardRef } from '@angular/core';
import {
  AbstractControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  ControlValueAccessor
} from '@angular/forms';
import { SharedModule } from '../../shared.module';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';

export interface AutoCompleteObject {
  label: string;
  item: any;
}

@Component({
  selector: 'app-material-auto-complete',
  templateUrl: './material-auto-complete.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SharedModule,
    MatInputModule,
    MatAutocompleteModule,
    MatFormFieldModule
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MaterialAutoCompleteComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => MaterialAutoCompleteComponent),
      multi: true,
    },
  ],
})

export class MaterialAutoCompleteComponent
implements ControlValueAccessor, Validator {

  onChange = (_value: any) => { };
  onTouched = () => { };

  private _formatter: (r: any) => string = (r) =>
    typeof r === 'string' ? r : '';

  @Input()
  set formatter(value: (r: any) => string) {
    this._formatter = value ?? ((r) =>
      typeof r === 'string' ? r : '');
    this.updateItems();
  }

  get formatter(): (r: any) => string {
    return this._formatter;
  }

  @Input() id: string = this.randomiseIds(10);
  @Input() set placeholder(value: string) {
    if (value) {
      this._placeholder = value;
    }
  }

  @Input() set disabled(value: boolean) {
    this._disabled = value;
  }

  private _rawItems: any[] = [];

  @Input()
  set items(value: any[]) {
    this._rawItems = value ?? [];
    this.updateItems();
  }

  private updateItems(): void {
    this._itemsList = this._rawItems.map(item => ({
      label: this._formatter(item) ?? '',
      item: item
    }));

    this._filteredList = [...this._itemsList];

  }

  @Input() required = false;

  _disabled: boolean = false;
  _itemsList: AutoCompleteObject[] = [];
  _filteredList: AutoCompleteObject[] = [];
  _value: string | number = '';
  _placeholder = '';
  _type = '';

  writeValue(value: any): void {
    if (value === null || value === undefined) {
      this._value = '';
      return;
    }

    if (typeof value === 'string') {
      this._value = value;
      return;
    }

    this._value = this.formatter(value);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  randomiseIds(length: number) {
    let result = '';
    const characters =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    let counter = 0;
    while (counter < length) {
      result += characters.charAt(
        Math.floor(Math.random() * charactersLength)
      );
      counter += 1;
    }
    return result;
  }

  valueChanged(event: any) {
    const text = String(event ?? '');
    const selectedItem = this._itemsList.find(o => o.label && (o.label.replace(/[\t\n ]/g, '') === event.replace(/[\t\n ]/g, '')));
    this._filteredList = this._itemsList.filter(i => i.label.toLocaleLowerCase().includes(event.toLocaleLowerCase()));
    if (selectedItem) {
      this._value = selectedItem.label;
      this.onChange(selectedItem.item);
    } else {
      this._value = text;
      this.onChange(null);
    }
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    if (this.required && !this._value) {
      return { required: true };
    }
    return null;
  }
}